import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { indigo } from "@mui/material/colors";
import dayjs from "dayjs";
import { getProductionForecastAbsoluteErrors } from "api/resources/productionForecasts";
import { ProductionPrognoseThirtyDayAbsoluteError } from "api/models/ProductionForecast";
import { BarChart } from "@mui/x-charts/BarChart";

interface PastForecastsAbsoluteErrorChartsProps {
  filters: any;
}

const PastForecastsAbsoluteErrorChart = ({
  filters,
}: PastForecastsAbsoluteErrorChartsProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dataset, setDataset] = useState<
    {
      day: string;
      forecaError?: number;
      aiolosError: number;
      meteologicaError?: number;
      realProduction: number;
    }[]
  >([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const response = await getProductionForecastAbsoluteErrors(filters);

        const transformedData = response.data.map(
          (item: ProductionPrognoseThirtyDayAbsoluteError) => ({
            day: dayjs(item.date).format("DD.MM"),
            forecaError: Math.round((item.forecaError / 1000) * 100) / 100,
            aiolosError: Math.round((item.aiolosError / 1000) * 100) / 100,
            ...(item.meteologicaError && {
              meteologicaError:
                Math.round((item.meteologicaError / 1000) * 100) / 100,
            }),
            realProduction:
              Math.round((item.realProduction / 1000) * 100) / 100,
          }),
        );
        setDataset(transformedData);
      } catch (error) {
        console.error("Error fetching chart data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const series = useMemo(() => {
    const base = [
      {
        dataKey: "realProduction",
        label: "Real production",
        color: indigo[400],
      },
      {
        dataKey: "aiolosError",
        label: "Aiolos error",
        color: "#9ddba1",
      },
    ];

    if (filters.type !== "SOLAR") {
      base.push({
        dataKey: "meteologicaError",
        label: "Meteologica error",
        color: "#f9f098",
      });
    } else {
    }

    if (filters.type !== "WIND") {
      base.push({
        dataKey: "forecaError",
        label: "Foreca error",
        color: "#d0a8ff",
      });
    }

    return base;
  }, [filters]);

  return (
    <Box>
      {loading && <Typography>Loading chart...</Typography>}
      {!loading && dataset.length === 0 && (
        <Typography>No data available.</Typography>
      )}
      {!loading && dataset.length > 0 && (
        <BarChart
          xAxis={[{ scaleType: "band", dataKey: "day" }]}
          dataset={dataset}
          series={series}
          margin={{ left: 40, right: 0, top: 40, bottom: 80 }}
          slotProps={{
            legend: {
              position: {
                vertical: "bottom",
                horizontal: "middle",
              },
              itemMarkWidth: 15,
              itemMarkHeight: 2,
              markGap: 5,
              itemGap: 5,
            },
          }}
          sx={{
            "& .MuiChartsLegend-series text": { fontSize: "12px !important" },
            padding: "5px",
          }}
          height={300}
        />
      )}
    </Box>
  );
};

export default PastForecastsAbsoluteErrorChart;
